/**
 * Helper function to format location addresses consistently across the application
 * Format: address, city, state zipcode (without USA or country)
 */

interface LocationAddress {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

/**
 * Formats a location object into a consistent address string
 * @param location - Object containing address components
 * @param options - Optional configuration for formatting
 * @returns Formatted address string in the format: "address, city, state zipcode"
 */
export const formatAddress = (
  location: LocationAddress | null | undefined,
  options?: {
    includeAddress?: boolean; // Default: true
    includeCity?: boolean; // Default: true
    includeState?: boolean; // Default: true
    includeZipCode?: boolean; // Default: true
  },
): string => {
  if (!location) {
    return '';
  }

  const {
    includeAddress = true,
    includeCity = true,
    includeState = true,
    includeZipCode = true,
  } = options || {};

  const parts: string[] = [];

  if (includeAddress && location.address) {
    const sanitized = sanitizeAddressString(location.address.trim());

    if (
      addressContainsStructuredParts(sanitized, location.city, location.state, location.zipCode)
    ) {
      return parseAddress(sanitized) || sanitized;
    }

    parts.push(sanitized);
  }

  const cityStateParts: string[] = [];

  if (includeCity && location.city) {
    cityStateParts.push(location.city.trim());
  }

  if (includeState && location.state) {
    cityStateParts.push(location.state.trim());
  }

  if (cityStateParts.length > 0) {
    parts.push(cityStateParts.join(', '));
  }

  if (includeZipCode && location.zipCode) {
    parts.push(location.zipCode.trim());
  }

  const joined = parts.join(', ');
  return parseAddress(joined) || joined;
};

/**
 * Removes trailing country names like ", USA" or ", United States"
 */
export const sanitizeAddressString = (value: string): string => {
  if (!value) return value;
  return value.replace(/,\s*USA$/i, '').replace(/,\s*United States$/i, '');
};

const addressContainsStructuredParts = (
  address: string,
  city?: string | null,
  state?: string | null,
  zipCode?: string | null,
): boolean => {
  const normalized = address.toLowerCase();
  const hasCity = Boolean(city?.trim() && normalized.includes(city.trim().toLowerCase()));
  const hasState = Boolean(state?.trim() && normalized.includes(state.trim().toLowerCase()));
  const hasZip = Boolean(zipCode?.trim() && normalized.includes(zipCode.trim()));
  return (hasCity && hasState) || (hasState && hasZip);
};

/** Street line only — used before createLocation / updateLocation (backend parity). */
export const extractStreetLineFromFullAddress = (
  fullAddress: string,
  city?: string | null,
  state?: string | null,
  zipCode?: string | null,
): string => {
  const sanitized = sanitizeAddressString(fullAddress.trim());
  const segments = sanitized
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (segments.length <= 1) {
    return sanitized;
  }

  const cityLower = city?.trim().toLowerCase() ?? '';
  const stateLower = state?.trim().toLowerCase() ?? '';
  const zip = zipCode?.trim() ?? '';

  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    const segmentLower = segment.toLowerCase();

    if (cityLower && segmentLower === cityLower) {
      return segments.slice(0, i).join(', ');
    }

    if (stateLower && zip) {
      const stateZipPattern = new RegExp(
        `^${stateLower}\\s+${zip.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
        'i',
      );
      if (stateZipPattern.test(segmentLower)) {
        return segments.slice(0, i).join(', ');
      }
    }

    if (stateLower && segmentLower === stateLower) {
      return segments.slice(0, i).join(', ');
    }
  }

  return segments[0];
};

export const streetAddressForLocationSave = (
  address: string,
  city?: string | null,
  state?: string | null,
  zipCode?: string | null,
): string => {
  const sanitized = sanitizeAddressString(address.trim());
  if (!sanitized) return sanitized;
  if (addressContainsStructuredParts(sanitized, city, state, zipCode)) {
    return extractStreetLineFromFullAddress(sanitized, city, state, zipCode);
  }
  return sanitized;
};

export interface ParsedLocationAddressFields {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

/** Parse Google formatted_address into structured fields (Add Car / geocode parity). */
export const extractLocationAddressFieldsFromFormatted = (
  formattedAddress: string,
  options?: {
    zipCode?: string | null;
    city?: string | null;
    state?: string | null;
  },
): ParsedLocationAddressFields => {
  const formattedAddr = formattedAddress.trim();
  const addressParts = formattedAddr
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  let streetAddress = addressParts[0] || formattedAddr;
  let city = options?.city?.trim() || '';
  let state = options?.state?.trim() || '';
  let zipCode = options?.zipCode?.trim() || '';

  if (addressParts.length >= 2) {
    if (!city) {
      city = addressParts[1] || '';
    }

    if (addressParts.length >= 3) {
      const lastPart = addressParts[addressParts.length - 1];
      // Spec 028: a US ZIP or a Canadian postal code ("ON M5V 3L9").
      const stateZipMatch = lastPart.match(
        /([A-Z]{2})\s+(\d{5}(?:-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d)/,
      );
      if (stateZipMatch) {
        state = stateZipMatch[1];
        zipCode = stateZipMatch[2] || zipCode;
      } else {
        const stateMatch = lastPart.match(/^([A-Z]{2})$/);
        if (stateMatch) {
          state = stateMatch[1];
        } else {
          state = lastPart.split(/\s+/)[0] || state;
          const zipMatch = lastPart.match(/\d{5}(-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d/);
          if (zipMatch) {
            zipCode = zipMatch[0];
          }
        }
      }
    }
  }

  if (addressContainsStructuredParts(sanitizeAddressString(formattedAddr), city, state, zipCode)) {
    streetAddress = extractStreetLineFromFullAddress(formattedAddr, city, state, zipCode);
  }

  return {
    streetAddress,
    city: city || 'Unknown',
    state: state || 'Unknown',
    zipCode,
  };
};

/**
 * Formats a location object for display with name and address
 * @param location - Object containing name and address components
 * @returns Formatted string in the format: "name: address, city, state zipcode"
 */
export const formatLocationWithName = (
  location: (LocationAddress & { name?: string | null }) | null | undefined,
): string => {
  if (!location) {
    return '';
  }

  const addressPart = formatAddress(location);

  if (location.name) {
    return addressPart ? `${location.name}: ${addressPart}` : location.name;
  }

  return addressPart;
};

/**
 * Formats city and state only (useful for car cards where full address is too long)
 * @param location - Object containing city and state
 * @returns Formatted string in the format: "city, state"
 */
export const formatCityState = (
  location: Pick<LocationAddress, 'city' | 'state'> | null | undefined,
): string => {
  if (!location) {
    return '';
  }

  const parts: string[] = [];

  if (location.city) {
    parts.push(location.city.trim());
  }

  if (location.state) {
    parts.push(location.state.trim());
  }

  return parts.join(', ');
};

/**
 * Parses and cleans an address string by removing duplicate parts and country names
 * @param addressString - Raw address string that may contain duplicates and country names
 * @returns Cleaned address string without duplicates or country names
 * @example
 * parseAddress("36-36 prince st, flushing CA NY 11354, USA Flushing, NY 11354")
 * // Returns: "36-36 prince st, flushing CA NY 11354"
 */
export const parseAddress = (addressString: string): string => {
  if (!addressString) return '';

  // Remove country names (USA, United States, etc.) - handle various formats
  let cleaned = addressString
    .replace(/,\s*USA\s*,?/gi, ',')
    .replace(/,\s*United States\s*,?/gi, ',')
    .replace(/\bUSA\b\s*,?/gi, '')
    .replace(/\bUnited States\b\s*,?/gi, '')
    .trim();

  // Split by comma or newline, but preserve the structure
  const segments = cleaned
    .split(/[,\n]/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  if (segments.length === 0) return '';

  // Pattern to match city, state, zip in various formats:
  // - "Flushing, NY 11354"
  // - "flushing CA NY 11354" (city state zip)
  // - "NY 11354" (just state zip)
  // Spec 028: the ZIP part also takes a Canadian postal code ("Toronto, ON M5V 3L9").
  const cityStateZipPattern =
    /^([^,]+),?\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d)$/i;
  const stateZipPattern = /^([A-Z]{2})\s+(\d{5}(?:-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d)$/i;

  // Track seen city-state-zip and state-zip combinations to detect duplicates
  const seenCityStateZip = new Set<string>();
  const seenStateZip = new Set<string>();
  const result: string[] = [];

  for (const segment of segments) {
    const cityStateZipMatch = segment.match(cityStateZipPattern);
    const stateZipMatch = segment.match(stateZipPattern);

    if (cityStateZipMatch) {
      // This segment contains city, state, zip
      const city = cityStateZipMatch[1].trim();
      const state = cityStateZipMatch[2].toUpperCase();
      const zip = cityStateZipMatch[3];
      const key = `${city.toLowerCase()}, ${state} ${zip}`;

      // Only add if we haven't seen this city-state-zip combination before
      if (!seenCityStateZip.has(key)) {
        seenCityStateZip.add(key);
        result.push(segment);
      }
      // Skip if duplicate
    } else if (stateZipMatch) {
      // This segment is just state and zip (e.g., "NY 11354")
      const state = stateZipMatch[1].toUpperCase();
      const zip = stateZipMatch[2];
      const key = `${state} ${zip}`;

      // Only add if we haven't seen this state-zip combination before
      if (!seenStateZip.has(key)) {
        seenStateZip.add(key);
        result.push(segment);
      }
      // Skip if duplicate
    } else {
      // This segment is likely part of the street address or city name
      // Check if it's a duplicate of an existing segment
      const isDuplicate = result.some(existing => {
        const existingLower = existing.toLowerCase();
        const segmentLower = segment.toLowerCase();
        // Check for exact match or if one contains the other (for partial duplicates)
        return (
          existingLower === segmentLower ||
          (existingLower.length > 5 &&
            segmentLower.length > 5 &&
            (existingLower.includes(segmentLower) || segmentLower.includes(existingLower)))
        );
      });

      if (!isDuplicate) {
        result.push(segment);
      }
    }
  }

  // Join and clean up
  let finalResult = result.join(', ');

  // Remove any double commas and trim
  finalResult = finalResult
    .replace(/,\s*,+/g, ',')
    .replace(/^,\s*|,\s*$/g, '')
    .trim();

  return finalResult;
};

/**
 * Formats a booking pickup/return location for display.
 * Uses structured fields when address is street-only; avoids duplicating city/state/zip
 * when address already came from Google Places formatted_address.
 */
export const formatBookingLocationDisplay = (
  location: (LocationAddress & { name?: string | null }) | null | undefined,
  fallback?: (LocationAddress & { name?: string | null }) | null,
): string => {
  const resolved = location ?? fallback;
  if (!resolved) {
    return 'N/A';
  }

  const formatted = formatAddress(resolved);
  if (formatted) {
    return formatted;
  }

  return resolved.name?.trim() || 'N/A';
};

export const buildGoogleMapsDirectionsUrl = (destination: string): string =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;

/** City/state/zip only — pre-booking map search must not include street address. */
export const formatObscuredLocationQuery = (location: LocationAddress | null | undefined): string =>
  formatAddress(location, {
    includeAddress: false,
    includeCity: true,
    includeState: true,
    includeZipCode: true,
  });

export const buildGoogleMapsSearchUrl = (query: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
